import { useState, useMemo, useCallback } from 'react';
import type { PresetDateOption } from '../components/DateMenu';

export type DateRange = {
  start: Date | null;
  end: Date | null;
};

type PresetOption = Exclude<PresetDateOption, 'custom'>;

export const getPresetDateRange = (option: PresetOption): DateRange => {
  if (option === 'inception') {
    return { start: null, end: null };
  }
  switch (option) {
    case '4w':
      return {
        start: new Date(new Date().setDate(new Date().getDate() - 28)),
        end: new Date(),
      };
    case '3m':
      return {
        start: new Date(new Date().setMonth(new Date().getMonth() - 3)),
        end: new Date(),
      };
    case '1y':
      return {
        start: new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
        end: new Date(),
      };
    case 'ytd':
      return {
        start: new Date(new Date().setMonth(0, 1)),
        end: new Date(),
      };
    case '1yago':
      return {
        start: new Date(new Date().getFullYear() - 1, 0, 1),
        end: new Date(new Date().getFullYear() - 1, 11, 31),
      };
    case '2yago':
      return {
        start: new Date(new Date().getFullYear() - 2, 0, 1),
        end: new Date(new Date().getFullYear() - 2, 11, 31),
      };
  }
};

const DEFAULT_SELECTED_OPTION: PresetDateOption = 'inception';

export const useDateRangeState = () => {
  const [selectedOption, setSelectedOption] = useState<PresetDateOption>(
    DEFAULT_SELECTED_OPTION
  );
  const [customDateRange, setCustomDateRange] = useState<DateRange>({
    start: new Date(),
    end: new Date(),
  });

  const dateRange = useMemo(
    () =>
      selectedOption === 'custom'
        ? customDateRange
        : getPresetDateRange(selectedOption),
    [selectedOption, customDateRange]
  );

  const onCustomDateRangeChange = useCallback((dateRange: DateRange) => {
    setCustomDateRange(dateRange);
  }, []);

  const _setSelectedOption = useCallback((option: PresetDateOption) => {
    setSelectedOption(option);
    if (option !== 'custom') {
      // Sync the custom date range to the preset option date range
      setCustomDateRange(getPresetDateRange(option));
    }
  }, []);

  return {
    selectedOption,
    setSelectedOption: _setSelectedOption,
    dateRange,
    onCustomDateRangeChange,
  };
};
